import { NotificationWorkspace } from "./components/notifications/NotificationWorkspace";
import { permissionNames as P } from './auth/permissions.generated';
import { productManagementView } from "./components/product-management/productManagementRoutes";
import { readLocationState, setConceptUrlState } from "./navigation/locationState";
import React, { useState, useEffect } from "react";
import { trackPageView } from "./analytics/mixpanel";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { AuthGuard } from "./components/AuthGuard";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { Navigation, type NavigationItem } from "./components/Navigation";
import { HeaderBar } from "./components/layout/HeaderBar";
import { PageContainer } from "./components/layout/PageContainer";
import { FeedbackProvider } from "./components/feedback/FeedbackContext";
import { FeedbackWorkspace } from "./components/feedback/FeedbackWorkspace";

// Main Dashboard Components
import { BakeryHub } from "./components/BakeryHub";

// Demand Management Components
import { DemandDashboard } from "./components/demand-management/DemandDashboard";
import { DemandIntakeForm } from "./components/demand-management/DemandIntakeForm";
import { DemandBacklogInterface } from "./components/DemandBacklogInterface";
import { BoardGovernance } from "./components/BoardGovernance";
import { ProjectPortfolio } from "./components/ProjectPortfolio";
import { ReportingAnalytics } from "./components/ReportingAnalytics";

// Campaign Management Components
import CampaignDashboard from "./components/campaign-management/CampaignDashboard";
import CreateCampaign from "./components/campaign-management/CreateCampaign";
import EditCampaign from "./components/campaign-management/EditCampaign";
import ArticleAssistantChat from "./components/product-management/ArticleAssistantChat";
import { TargetProfileManagement } from "./components/campaign-management/TargetProfileManagement";
import BrandManagement from "./components/brand-management/BrandManagement";
import CampaignAdVariations from "./components/campaign-management/CampaignAdVariations";
import StyleForgeStudio from "./components/campaign-management/StyleForgeStudio";
import {
    CampaignShowcasePage,
    type CampaignShowcaseLayout,
} from "./components/campaign-management/CampaignShowcasePage";

import { CollectionsPage } from "./components/product-collections/CollectionsPage";
import { requestCollectionNavigation } from "./components/product-collections/collections";

// Product Management Components
import { ProductDashboard } from "./components/product-management/ProductDashboard";
import { BusinessRuleManagement } from "./components/product-management/BusinessRuleManagement";
import { BulkPDFGeneration } from "./components/product-management/BulkPDFGeneration";
import CatalogManagement from "./components/catalog-management/CatalogManagement";
import ProductComparer from "./components/product-comparer/ProductComparer";
import { ProductSearchPage } from "./components/product-search/ProductSearchPage";
import { ProductDetailPage } from "./components/product-detail/ProductDetailPage";
import { productEntityId, PRODUCT_NAVIGATION_EVENT } from "./components/product-detail/navigation";
import { ProductSearchSyncPage } from "./components/product-management/ProductSearchSyncPage";

// Concept Management Components
import { ConceptDashboard } from "./components/concept-management/ConceptDashboard";
import { ConceptDetail } from "./components/concept-management/ConceptDetail";
import { ConceptForm } from "./components/concept-management/ConceptForm";

// HubSpot Management Components
import { HubSpotDashboard } from "./components/hubspot-management/HubSpotDashboard";
import { HubSpotBackfilling } from "./components/hubspot-management/HubSpotBackfillContacts";
import { BackfillForms } from "./components/hubspot-management/HubSpotBackfillForms";
import { HubSpotEmailMetricsBackfill } from "./components/hubspot-management/HubSpotEmailMetricsBackfill";

// System Components
import { Settings } from "./components/Settings";
import { ProfilePage } from "./components/ProfilePage";

function DashboardApp() {
    const [currentView, setCurrentView] = useState<NavigationItem>(() => readLocationState(window.location).currentView);
    const [searchNavigation, setSearchNavigation] = useState(0);
    const [selectedProductId, setSelectedProductId] = useState(() => productEntityId(window.location.pathname));
    const [editCampaignId, setEditCampaignId] = useState<string | null>(null);
    const [campaignShowcaseId, setCampaignShowcaseId] = useState<string | null>(null);
    const [campaignShowcaseLayout, setCampaignShowcaseLayout] = useState<CampaignShowcaseLayout>("spotlight");
    const [isUrlInitialized, setIsUrlInitialized] = useState(false);
    const [selectedConceptId, setSelectedConceptId] = useState<number | null>(null);
    const [pendingChanges, setPendingChanges] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    useEffect(() => {
        if (isUrlInitialized && currentView !== 'feedback') trackPageView(currentView,
            currentView === 'product-detail' ? selectedProductId
            : currentView === 'edit-campaign' ? editCampaignId
            : currentView === 'campaign-showcase' ? campaignShowcaseId
            : selectedConceptId);
    }, [currentView, selectedProductId, editCampaignId, selectedConceptId, campaignShowcaseId, isUrlInitialized]);

    useEffect(() => {
        const restored = readLocationState(window.location);
        setCurrentView(restored.currentView);
        setSelectedProductId(restored.selectedProductId);
        setEditCampaignId(restored.editCampaignId);
        setCampaignShowcaseId(restored.campaignShowcaseId);
        setCampaignShowcaseLayout(restored.campaignShowcaseLayout);
        setSelectedConceptId(restored.selectedConceptId);
        setIsUrlInitialized(true);
    }, []);

    useEffect(() => {
        const handlePopState = () => {
            setSearchNavigation(value => value + 1);
            const restored = readLocationState(window.location);
            setCurrentView(restored.currentView);
            setSelectedProductId(restored.selectedProductId);
            setEditCampaignId(restored.editCampaignId);
            setCampaignShowcaseId(restored.campaignShowcaseId);
            setCampaignShowcaseLayout(restored.campaignShowcaseLayout);
            setSelectedConceptId(restored.selectedConceptId);
        };
        window.addEventListener("popstate", handlePopState);
        window.addEventListener(PRODUCT_NAVIGATION_EVENT, handlePopState);
        return () => {
            window.removeEventListener("popstate", handlePopState);
            window.removeEventListener(PRODUCT_NAVIGATION_EVENT, handlePopState);
        };
    }, []);

    useEffect(() => {
        if (!isUrlInitialized) {
            return;
        }

        if (currentView === "feedback") {
            if (!window.location.pathname.startsWith('/feedback')) window.history.pushState(null, '', '/feedback');
            return;
        }
        if (currentView === "product-detail") return;

        if (currentView === "product-search") {
            if (window.location.pathname !== "/product-management/search") {
                window.history.pushState(null, "", "/product-management/search?page=1&pageSize=20");
            }
            return;
        }

        if (currentView === "product-collections") {
            if (window.location.pathname !== "/product-management/collections") window.history.pushState(null, "", "/product-management/collections");
            return;
        }
        if (currentView === "product-sync") {
            if (window.location.pathname !== "/product-management/synchronization") {
                window.history.pushState(null, "", "/product-management/synchronization");
            }
            return;
        }

        const params = new URLSearchParams(window.location.search);
        params.set("view", currentView);
        setConceptUrlState(params, currentView, selectedConceptId);

        if (currentView === "edit-campaign" && editCampaignId) {
            params.set("campaignId", editCampaignId);
            params.delete("layout");
        } else if (currentView === "campaign-showcase") {
            if (campaignShowcaseId) {
                params.set("campaignId", campaignShowcaseId);
            } else {
                params.delete("campaignId");
            }
            params.set("layout", campaignShowcaseLayout);
        } else {
            params.delete("campaignId");
            params.delete("layout");
        }

        const paramsString = params.toString();
        const newUrl = paramsString ? `/?${paramsString}` : "/";
        window.history.replaceState(null, "", newUrl);
    }, [
        campaignShowcaseId,
        campaignShowcaseLayout,
        currentView,
        editCampaignId,
        isUrlInitialized,
        selectedConceptId,
    ]);

    const applyNavigation = (view: NavigationItem, campaignId?: string, conceptId?: number) => {
        setCurrentView(view);

        // Handle campaign ID for edit-campaign route
        if (view === "edit-campaign" && campaignId) {
            setEditCampaignId(campaignId);
        } else {
            setEditCampaignId(null);
        }

        if (view === "campaign-showcase") {
            setCampaignShowcaseId(campaignId ?? null);
        } else {
            setCampaignShowcaseId(null);
        }

        if (view === "concept-detail" || view === "edit-concept") {
            setSelectedConceptId(conceptId ?? null);
        } else {
            setSelectedConceptId(null);
        }

        setPendingChanges(false);
    };

    const handleNavigation = (view: NavigationItem, campaignId?: string, conceptId?: number) => {
        requestCollectionNavigation(() => applyNavigation(view, campaignId, conceptId));
    };

    const handleBackToDashboard = () => {
        setCurrentView("dashboard");
        setEditCampaignId(null);
        setCampaignShowcaseId(null);
        setSelectedConceptId(null);
        setPendingChanges(false);
    };

    const handleReturnToCampaignDashboard = () => {
        setCurrentView("campaign-dashboard");
        setEditCampaignId(null);
        setCampaignShowcaseId(null);
        setSelectedConceptId(null);
        setPendingChanges(false);
    };

    const handleDemandSuccess = () => {
        toast.success("Demand submitted successfully!", {
            description: "Your demand has been submitted for review and you'll receive updates via email.",
        });
        handleBackToDashboard();
    };

    // Track form changes for demand intake
    useEffect(() => {
        if (currentView === "create-demand") {
            setPendingChanges(true);
        }
    }, [currentView]);

    const renderWithLayout = (content: React.ReactNode, className?: string) => (
        <PageContainer className={className}>{content}</PageContainer>
    );

    const renderContent = () => {
        switch (currentView) {
            case "notifications":
                return renderWithLayout(<NotificationWorkspace />);
            case "feedback":
                return renderWithLayout(<FeedbackWorkspace />);
            // Demand Management Section
            case "demand-dashboard":
                return renderWithLayout(
                    <DemandDashboard
                        onNavigateBack={handleBackToDashboard}
                        onNavigateToFeature={handleNavigation}
                    />,
                );
            case "create-demand":
                return renderWithLayout(
                    <ProtectedRoute permission="canCreateDemands">
                        <DemandIntakeForm
                            onCancel={handleBackToDashboard}
                            onSuccess={handleDemandSuccess}
                        />
                    </ProtectedRoute>,
                );
            case "demand-backlog":
                return renderWithLayout(
                    <ProtectedRoute permission="canViewDemandBacklog">
                        <DemandBacklogInterface onNavigateBack={handleBackToDashboard} />
                    </ProtectedRoute>,
                );
            case "board-governance":
                return renderWithLayout(
                    <ProtectedRoute permission="canViewBoardGovernance">
                        <BoardGovernance onNavigateBack={handleBackToDashboard} />
                    </ProtectedRoute>,
                );
            case "project-portfolio":
                return renderWithLayout(
                    <ProtectedRoute permission="canViewProjectPortfolio">
                        <ProjectPortfolio onNavigateBack={handleBackToDashboard} />
                    </ProtectedRoute>,
                );
            case "reports":
                return renderWithLayout(
                    <ProtectedRoute permission="canViewReports">
                        <ReportingAnalytics onNavigateBack={handleBackToDashboard} />
                    </ProtectedRoute>,
                );

            // Campaign Management Section
            case "campaign-dashboard":
                return renderWithLayout(
                    <CampaignDashboard onNavigate={handleNavigation} />,
                );
            case "create-campaign":
                return renderWithLayout(
                    <ProtectedRoute permission="canAddCampaigns">
                        <CreateCampaign onNavigate={handleNavigation} />
                    </ProtectedRoute>,
                );
            case "edit-campaign":
                return renderWithLayout(
                    <ProtectedRoute permission="canUpdateCampaigns">
                        {editCampaignId ? (
                            <EditCampaign
                                campaignId={editCampaignId}
                                onNavigate={handleNavigation}
                            />
                        ) : (
                            <div className="p-6 text-center text-red-500">
                                Campaign ID not found. Please go back to the campaign dashboard.
                            </div>
                        )}
                    </ProtectedRoute>,
                );
            case "article-assistant-chat":
                return renderWithLayout(
                    <ProtectedRoute permission="canReadProductSearch">
                        <ArticleAssistantChat />
                    </ProtectedRoute>,
                );
            case "campaign-ad-variations":
                return renderWithLayout(
                    <ProtectedRoute permission="canAccessCampaignManagement">
                        <CampaignAdVariations onNavigate={handleNavigation} />
                    </ProtectedRoute>,
                );
            case "styled-images-studio":
                return renderWithLayout(
                    <ProtectedRoute capability={P.CampaignStyleRead}>
                        <StyleForgeStudio onNavigate={handleNavigation} />
                    </ProtectedRoute>,
                );
            case "campaign-showcase":
                return renderWithLayout(
                    <ProtectedRoute permission="canAccessCampaignManagement">
                        <CampaignShowcasePage
                            campaignId={campaignShowcaseId}
                            layout={campaignShowcaseLayout}
                            onLayoutChange={setCampaignShowcaseLayout}
                            onReturnToDashboard={handleReturnToCampaignDashboard}
                        />
                    </ProtectedRoute>,
                    "bg-[#f8f5f0] p-6 min-h-screen"
                );
            case "brand-management":
                return renderWithLayout(
                    <ProtectedRoute capability={P.CampaignBrandRead}>
                        <BrandManagement onNavigate={handleNavigation} />
                    </ProtectedRoute>,
                );
            case "target-profile-management":
                return renderWithLayout(
                    <ProtectedRoute capability={P.CampaignTargetProfileRead}>
                        <TargetProfileManagement onNavigate={handleNavigation} />
                    </ProtectedRoute>,
                );

            // Product Management Section
            case "product-dashboard":
                return renderWithLayout(
                    <ProductDashboard
                        onNavigateToFeature={handleNavigation}
                    />,
                );
            case "business-rule-management":
                return renderWithLayout(
                    <ProtectedRoute anyCapability={[P.ProductBusinessRuleRead, P.ProductFieldRestrictionRead, P.ProductInriverRoleAssignmentRead]}>
                        <BusinessRuleManagement onNavigate={handleNavigation} />
                    </ProtectedRoute>,
                );
            case "bulk-pdf-generation":
                return renderWithLayout(
                    <ProtectedRoute permission="canGeneratePDFs">
                        <BulkPDFGeneration onNavigateBack={handleBackToDashboard} />
                    </ProtectedRoute>,
                );
            case "catalog-management":
                return renderWithLayout(
                    <ProtectedRoute capability={P.ProductCatalogTemplateRead}>
                        <CatalogManagement onNavigate={handleNavigation} />
                    </ProtectedRoute>,
                );
            case "product-comparer":
                return renderWithLayout(
                    <ProtectedRoute capability={P.ProductComparisonRead}>
                        <ProductComparer onNavigate={handleNavigation} />
                    </ProtectedRoute>,
                );
            case "product-collections":
                return renderWithLayout(<ProtectedRoute permission="canReadCollections"><CollectionsPage /></ProtectedRoute>);
            case "product-search":
                return (
                    <ProtectedRoute permission="canReadProductSearch">
                        <ProductSearchPage key={searchNavigation} />
                    </ProtectedRoute>
                );
            case "product-detail":
                return <ProtectedRoute permission="canReadProductSearch"><ProductDetailPage entityId={selectedProductId}/></ProtectedRoute>;
            case "product-sync":
                return renderWithLayout(
                    <ProtectedRoute permission="canManageProductSearchSync">
                        <ProductSearchSyncPage />
                    </ProtectedRoute>,
                );

            case "concept-dashboard":
                return renderWithLayout(
                    <ProtectedRoute permission="canAccessConceptManagement">
                        <ConceptDashboard onNavigate={handleNavigation} />
                    </ProtectedRoute>,
                );
            case "create-concept":
                return renderWithLayout(
                    <ProtectedRoute permission="canCreateConcepts">
                        <ConceptForm
                            mode="create"
                            onCancel={() => handleNavigation("concept-dashboard")}
                            onSuccess={(id) => handleNavigation("concept-detail", undefined, id)}
                        />
                    </ProtectedRoute>,
                );
            case "concept-detail":
                return renderWithLayout(
                    <ProtectedRoute permission="canAccessConceptManagement">
                        {selectedConceptId ? (
                            <ConceptDetail
                                conceptId={selectedConceptId}
                                onNavigate={handleNavigation}
                            />
                        ) : (
                            <div className="p-6 text-center text-red-500">
                                Concept not found. Please return to the concept overview.
                            </div>
                        )}
                    </ProtectedRoute>,
                );
            case "edit-concept":
                return renderWithLayout(
                    <ProtectedRoute permission="canUpdateConcepts">
                        {selectedConceptId ? (
                            <ConceptForm
                                mode="edit"
                                conceptId={selectedConceptId}
                                onCancel={() => handleNavigation("concept-detail", undefined, selectedConceptId)}
                                onSuccess={(id) => handleNavigation("concept-detail", undefined, id)}
                            />
                        ) : (
                            <div className="p-6 text-center text-red-500">
                                Concept not found. Please return to the concept overview.
                            </div>
                        )}
                    </ProtectedRoute>,
                );

            // HubSpot Management Section
            case "hubspot-dashboard":
                return renderWithLayout(
                    <ProtectedRoute permission="canManageHubSpot">
                        <HubSpotDashboard />
                    </ProtectedRoute>,
                );
            case "hubspot-backfilling":
                return renderWithLayout(
                    <ProtectedRoute permission="canManageHubSpot">
                        <HubSpotBackfilling />
                    </ProtectedRoute>,
                );
            case "hubspot-backfill-forms":
                return renderWithLayout(
                    <ProtectedRoute permission="canManageHubSpot">
                        <BackfillForms />
                    </ProtectedRoute>,
                );
            case "hubspot-email-metrics":
                return renderWithLayout(
                    <ProtectedRoute permission="canManageHubSpot">
                        <HubSpotEmailMetricsBackfill />
                    </ProtectedRoute>,
                );

            // System Section
            case "settings":
                return renderWithLayout(
                    <ProtectedRoute permission="isAdmin">
                        <Settings />
                    </ProtectedRoute>,
                );
            case "profile":
                return renderWithLayout(
                    <ProfilePage onNavigateBack={handleBackToDashboard} />,
                );

            // Main Dashboard
            case "dashboard":
                return renderWithLayout(<BakeryHub />);
            default:
                return renderWithLayout(
                    <div className="p-4 text-red-500">
                        Unknown view: {String(currentView)}<br />
                        Please contact support.
                    </div>,
                );
        }
    };

    return (
        <PermissionsProvider>
          <FeedbackProvider>
            <div className="flex h-screen w-full overflow-hidden bg-background">
                <Navigation
                    currentView={currentView}
                    onNavigate={handleNavigation}
                    pendingChanges={pendingChanges}
                    isMobileOpen={isMobileSidebarOpen}
                    onCloseMobile={() => setIsMobileSidebarOpen(false)}
                />
                <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                    <HeaderBar
                        isNavigationOpen={isMobileSidebarOpen}
                        onOpenNavigation={() => setIsMobileSidebarOpen(true)}
                        onNavigate={(view) => handleNavigation(view)}
                    />
                    <main className="flex-1 overflow-auto" data-app-scroll>
                        {currentView === "product-search" || currentView === "product-detail" ? renderContent() : <div className="h-full">
                            <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-4 md:py-6">
                                {renderContent()}
                            </div>
                        </div>}
                    </main>
                </div>
            </div>
          </FeedbackProvider>
        </PermissionsProvider>
    );
}

export default function App() {
    return (
        <>
            <AuthGuard>
                <DashboardApp />
            </AuthGuard>
            <Toaster />
        </>
    );
}
